import type { components } from "@contracts/generated/editor-service";

import { splitPartKey, type DraftPart } from "./assetContent";
import { manifestRoles, type LibraryAssetPartManifest } from "./namedParts";

export type ScreeningDocumentSchema =
  components["schemas"]["ScreeningDocumentSchema"];
export type ScreeningStepsDocument =
  components["schemas"]["ScreeningStepsDocument"];
export type ScreeningDiagnosisDocument =
  components["schemas"]["ScreeningDiagnosisDocument"];

/**
 * The boundary between a draft's structured part and #912's typed forms.
 *
 * ## Why the draft still holds JSON text
 *
 * `DraftPart.text` is a STRING for every part, and a structured one is seeded as
 * `JSON.stringify(part.data, null, 2)` and parsed back at `toPartsContent`
 * (`lib/assetContent.ts`). That is the shape `PartsEditor`, the BG set editor,
 * the main-concern rows and the questionnaire surface all share, and widening
 * `DraftPart` into a discriminated text/object union to suit two Pathway roles
 * would rewrite the draft model every one of those surfaces is built on — the
 * scoped-change-becomes-unreviewable trade `useNamedDraft` and `PathwayStepList`
 * both decline to make. So the parse/serialise pair lives HERE, at the one
 * surface that needs it, and the draft keeps its single representation.
 *
 * THE ROUND TRIP IS EXACT for what these documents hold. `screening_steps` and
 * `screening_diagnosis` carry integers, booleans and strings only — no floats,
 * no `undefined`, no dates — so `JSON.parse(JSON.stringify(x))` returns `x`. The
 * four values §5 of the S7 contract names as load-bearing (`0`, `false`, `999`
 * and an absent key) all survive it, and the pinned engine reads what was
 * stored: the service normalises nothing (`screening_documents.py`).
 *
 * ## Serialising with the seeding format, on purpose
 *
 * `serializeScreeningDocument` uses the same two-space `JSON.stringify` the
 * draft was seeded with. Dirty is decided on the TEXT ("Dirty is decided on what
 * the clinician typed, not on what it would serialise to") — so a form that
 * re-serialised compactly would mark a pristine draft dirty the instant it
 * rendered, and offer a Save that changes nothing but the whitespace of a
 * clinical document. Writes therefore happen on a real edit and in the draft's
 * own format.
 */

/** A document this module can read, once its schema has named which one it is. */
export type ScreeningDocument = ScreeningStepsDocument | ScreeningDiagnosisDocument;

/**
 * The schema the SERVICE publishes for a structured role, or `null`.
 *
 * Read off the part manifest rather than from a role list in this repository,
 * which is the whole reason `LibraryPartDefinition.structuredSchema` exists:
 * "how a client DISCOVERS a typed form instead of hardcoding a role list
 * (#911)". A hardcoded pair here would also be the frontend Editor-API
 * catalogue #300 deleted and left a build guard against.
 *
 * Absent means what it says — the role is accepted and its inside is
 * engine-owned — so this returns `null` and the caller keeps the protected
 * explanation. That covers `screening_schema`, the whole full-evaluation half,
 * and every role on a family whose type publishes no schemas at all.
 */
export function screeningSchemaForRole(
  entry: LibraryAssetPartManifest | null,
  role: string,
): ScreeningDocumentSchema | null {
  const definition = manifestRoles(entry).find((candidate) => candidate.role === role);
  return definition?.structuredSchema ?? null;
}

/**
 * Every structured role on this asset type for which the service publishes a
 * schema.
 *
 * WHAT A DRAFT ROW CANNOT KNOW BY ITSELF. The contract's `PathwayStep` carries
 * the server's own `editability`, so the CATALOG can render a typed step
 * correctly from the response alone. An open draft's rows are `namedParts`'
 * projection of the parts array, whose only vocabulary is the part KIND — so
 * over a draft, "structured" no longer answers "is this engine-owned" and
 * nothing in the row can answer it. This set is that answer, and it comes from
 * the same manifest the projection is derived from, so the two views cannot
 * disagree.
 *
 * Empty while the manifest read is pending or has failed, which is the safe
 * direction: a row keeps its existing protected presentation until the service
 * has actually said otherwise.
 */
export function typedScreeningRoles(
  entry: LibraryAssetPartManifest | null,
): Set<string> {
  return new Set(
    manifestRoles(entry)
      .filter((definition) => definition.structuredSchema != null)
      .map((definition) => definition.role),
  );
}

/**
 * A draft part's text as the typed document its schema names, or `null` when
 * this editor cannot round-trip it.
 *
 * Returns `null` rather than throwing, and the caller renders the untyped
 * fallback: a stored version can hold anything — versions are immutable,
 * undeletable and predate this schema — so a document that does not fit is a
 * state to EXPLAIN, never a crash on open. The service takes the same position
 * for the same reason (`StructuredPart` stays `JsonValue`; the gate runs on
 * write, not on read).
 *
 * ## What it checks, and why exactly this much
 *
 * NOT a second copy of the service's validator. Field-level authority is
 * `parse_screening_document`'s, which refuses with a pointer at the offending
 * field, and a weaker duplicate in the browser is one that can disagree with the
 * one that decides. What is checked here is narrower and different in kind: the
 * shape THIS FORM INDEXES, so that a document the form cannot render reaches the
 * unavailable state instead of a `TypeError`.
 *
 * `{"steps": {}}` is the concrete case. It is a document the write gate refuses
 * today but which an older version may already hold, and it reached
 * `steps.map(...)` and threw — an uncaught render error in place of the fallback
 * this function's contract promises.
 *
 * ## Integers this editor cannot carry
 *
 * A diagnosis threshold has NO upper bound on the server, deliberately: `999`
 * means "this step can never count", and a larger sentinel is equally valid.
 * JavaScript numbers are doubles, so an integer beyond `Number.MAX_SAFE_INTEGER`
 * cannot survive `JSON.parse` — `9007199254740993` reads back as
 * `…992`. Nothing in this form could then show the stored value, and saving an
 * unrelated flag would republish the rounded one.
 *
 * So a document containing such a literal is refused HERE, before any control
 * renders it. Detected on the raw text rather than on the parsed value, because
 * by the time it is parsed the evidence is gone.
 */
export function parseScreeningDocument(
  text: string,
  schema: ScreeningDocumentSchema,
): ScreeningDocument | null {
  if (hasUnrepresentableInteger(text)) {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  return schema === "screening_steps"
    ? renderableSteps(value)
    : renderableDiagnosis(value);
}

/** The steps document, when every field this form indexes is the right shape. */
function renderableSteps(value: Record<string, unknown>): ScreeningStepsDocument | null {
  const steps = value["steps"];
  if (!Array.isArray(steps)) {
    return null;
  }
  for (const step of steps) {
    // `Number.isInteger` excludes the booleans a bare `typeof === "number"`
    // would admit, and the instruction is a `<textarea>`'s value: anything else
    // renders as the string "[object Object]" over a clinical document.
    if (
      !isRecord(step) ||
      !Number.isInteger(step["id"]) ||
      typeof step["instruction"] !== "string"
    ) {
      return null;
    }
  }
  return value as ScreeningStepsDocument;
}

/** The diagnosis document, on the same terms. */
function renderableDiagnosis(
  value: Record<string, unknown>,
): ScreeningDiagnosisDocument | null {
  // ABSENT IS A STATE THIS FORM MUST BE ABLE TO SHOW, even for the one required
  // field: clearing the control mid-edit removes the key, and the form's own
  // "This value is required" message is what says so. Refusing the document
  // there would unmount the whole form under the cursor and replace it with an
  // unavailable state, which is not what a half-finished edit means. What is
  // refused is the wrong TYPE, which no edit on this screen can produce and
  // which the controls could not render.
  for (const field of ["yes_count", "mandatory_count"]) {
    if (value[field] !== undefined && !Number.isInteger(value[field])) {
      return null;
    }
  }
  if (!isRenderableMap(value["mandatory_roles"], (entry) => Number.isInteger(entry))) {
    return null;
  }
  if (
    !isRenderableMap(value["include_mandatory_yeses"], (entry) => typeof entry === "boolean")
  ) {
    return null;
  }
  return value as ScreeningDiagnosisDocument;
}

/** An absent mapping is fine; a present one must be an object of `entry`s. */
function isRenderableMap(
  value: unknown,
  entry: (candidate: unknown) => boolean,
): boolean {
  if (value === undefined) {
    return true;
  }
  return isRecord(value) && Object.values(value).every(entry);
}

/**
 * Whether the raw document text holds an integer literal a double cannot carry.
 *
 * Matched on the TEXT because `JSON.parse` has already rounded by the time a
 * value is inspectable, so the parsed number cannot report that it was changed.
 * Only plain integer literals are considered: `1.5` and `1e3` are not values
 * these two documents use, and treating them as unrepresentable would refuse a
 * document over a shape nothing stores.
 */
function hasUnrepresentableInteger(text: string): boolean {
  // Number literals OUTSIDE strings. A clinical instruction may contain any
  // digits at all, and refusing a document because a prompt mentions a long
  // number would be a false refusal on the most common field here.
  const withoutStrings = text.replace(/"(?:[^"\\]|\\.)*"/g, '""');
  for (const literal of withoutStrings.match(/-?\d+/g) ?? []) {
    if (!Number.isSafeInteger(Number(literal))) {
      return true;
    }
  }
  return false;
}

/**
 * The id a new screening step would take, or the reason it cannot have one.
 *
 * THE ONE PLACE THE ALLOCATION IS DECIDED, because the alternative shipped a
 * dead control: the refusal and the seed each answered this separately, agreed
 * on an unread history and DISAGREED on an exhausted id space. The refusal said
 * nothing (Add enabled), the seed returned `null`, and the click handler
 * returned early — an enabled button that did nothing, reachable on a
 * `pathway_variant` that satisfies the alternative required family while having
 * once claimed `MAX_STEP_ID`.
 *
 * Both callers now read this, so they cannot disagree: a refusal exists exactly
 * when no id does.
 */
export type StepIdAllocation =
  | { readonly id: number; readonly refusal?: undefined }
  | { readonly id?: undefined; readonly refusal: string };

export function nextScreeningStepId(
  reservedStepIds: ReadonlySet<number> | null,
): StepIdAllocation {
  if (reservedStepIds === null) {
    return {
      refusal:
        "This asset's published step ids have not been read, so a new screening step cannot be given one that is certainly free.",
    };
  }
  const next = Math.max(0, ...reservedStepIds) + 1;
  if (next > MAX_STEP_ID) {
    return {
      refusal: `This screening has used every step id up to ${String(MAX_STEP_ID)}, so no further step can be added.`,
    };
  }
  return { id: next };
}

/**
 * Why a typed role cannot be ADDED right now, or `null`.
 *
 * Only a `screening_steps` document allocates an identity, so only it can be
 * blocked: its new step must not be handed an id a retired step's
 * `mandatory_roles.stepN` still names, and there must be an id left to hand it.
 * A `screening_diagnosis` allocates nothing — `emptyScreeningDocument` seeds it
 * `{}` whatever the history says — so refusing it would disable Add for a role
 * that needs no id, and explain itself with a sentence about step ids.
 *
 * A pure function in `lib` on purpose: the only lane that exercises the unread
 * branch is one whose version-history read FAILS, which no component test
 * arranges — a live test for it passed against the broken code. Here it is
 * `src/lib`, which `pr-gate`'s `web-fast` job runs, so the rule is checked
 * before a merge rather than after one.
 */
export function screeningAddRefusal(
  schema: ScreeningDocumentSchema | null | undefined,
  reservedStepIds: ReadonlySet<number> | null,
): string | null {
  if (schema !== "screening_steps") {
    return null;
  }
  return nextScreeningStepId(reservedStepIds).refusal ?? null;
}

/**
 * The document a NEWLY ADDED typed role starts from.
 *
 * `addNamedRole` seeds every structured part `"{}"`, which is the right neutral
 * value for a role with no published schema — and the wrong one for these two.
 * `renderableSteps` refuses a document with no `steps` array, so an added
 * `screening_steps` opened as "not in the shape its schema describes" with no
 * control able to repair it, and `min_length=1` refused the save as well: an
 * Add whose only outcome was a dead row.
 *
 * The steps seed carries ONE EMPTY STEP, which is exactly what the form's own
 * Add button produces (`{ id: nextId, instruction: "" }`). It is not saveable
 * as it stands — `Instruction` is `min_length=1` — and it is not meant to be:
 * it is a row the clinician types into, and the form says so.
 *
 * `screening_diagnosis` keeps `{}`. `renderableDiagnosis` deliberately admits a
 * document with no `yes_count` so a half-finished edit does not unmount the
 * form, and the required-value message is what states the rest.
 */
export function emptyScreeningDocument(
  schema: ScreeningDocumentSchema,
  /**
   * Every step id this asset has ever claimed, for the steps seed.
   *
   * NOT OPTIONAL, and `null` means "not known" — the same contract
   * `ScreeningStepsForm.reservedStepIds` carries, because this is the same
   * allocation. Seeding `id: 1` unconditionally was the hole the first round
   * left: an asset can carry a `screening_diagnosis` whose `mandatory_roles`
   * still names `step1` from a version that retired it, so a brand-new blank
   * instruction would silently inherit that rule. The caller refuses the Add
   * when this is `null` rather than guessing.
   */
  reservedStepIds: ReadonlySet<number> | null,
): ScreeningDocument | null {
  if (schema === "screening_diagnosis") {
    return {} as ScreeningDiagnosisDocument;
  }
  if (schema !== "screening_steps") {
    // EXHAUSTIVE, so a third published schema fails the BUILD rather than
    // falling into a default that hands its form `{}` — the dead row this
    // function exists to prevent, reintroduced silently. `ScreeningDocumentSchema`
    // is a closed union, so this branch is unreachable today and `never` is what
    // proves it.
    const unreachable: never = schema;
    throw new Error(`No seed for screening schema ${String(unreachable)}`);
  }
  // THE SAME ALLOCATION the Add control refused on. `null` here means the
  // control should never have been enabled, and `screeningAddRefusal` is what
  // guarantees it was not.
  const allocation = nextScreeningStepId(reservedStepIds);
  return allocation.id === undefined
    ? null
    : { steps: [{ id: allocation.id, instruction: "" }] };
}

/**
 * The service's `StepId` upper bound (`1..9999`), mirrored for allocation.
 *
 * Pinned to it by `invariants/screeningStepRole.test.ts`, which runs before a
 * merge — the browser must not offer an id the write gate will refuse.
 */
export const MAX_STEP_ID = 9999;

/**
 * The document as draft text, in the format the draft was seeded with.
 *
 * See the module note: matching `toDraft`'s two-space spacing is what keeps an
 * untouched form from reporting itself dirty.
 */
export function serializeScreeningDocument(document: ScreeningDocument): string {
  return JSON.stringify(document, null, 2);
}

/**
 * The service's `_STEP_ROLE`, mirrored rather than approximated.
 *
 * `^step[1-9][0-9]{0,3}$` — no `step0`, no leading zero, at most four digits,
 * which is the `StepId` bound (`1..9999`) spelled as a role name.
 */
export const STEP_ROLE = /^step[1-9][0-9]{0,3}$/;

/**
 * The `stepN` names a diagnosis mapping in this draft may refer to, or `null`
 * when the draft declares none.
 *
 * DELIBERATELY THE SERVICE'S OWN RULE, mirrored so the refusal is visible before
 * Save rather than only after it: `screening_documents._declared_step_roles`
 * takes the UNION of the sibling `screening_steps` ids and the sibling
 * `screening_schema`'s own `steps` keys, falls back from a language-prefixed key
 * to a language-neutral one, and skips the check entirely when neither document
 * is present.
 *
 * Both halves matter. `screening_steps` carries the ids a clinician just
 * authored, which is where a typo lands. `screening_schema` is what the
 * classifier actually emits, is optional, and is frequently inherited — so a
 * rule that demanded it would refuse legitimate content. The two disagree in
 * real packs and the disagreement is benign: base canon's mania declares four
 * steps and three schema fields, because the terminal `<DONE_TOKEN>` step is
 * never classified. A mapping key naming such a step is inert at runtime, not
 * fatal, and both MOD and NII rely on exactly that when they set
 * `mandatory_roles.step3: 999`.
 *
 * This is a PREVIEW of the server's answer and never a gate of its own: the
 * form reports an unknown reference and still lets Save carry it, because the
 * service is the authority on whether a version is refused.
 */
export function declaredStepRoles(
  parts: readonly DraftPart[],
  language: string | null,
): Set<string> | null {
  const roles = new Set<string>();
  let known = false;

  const sibling = (role: string): unknown => {
    const match =
      parts.find((part) => {
        const [partLanguage, partRole] = splitPartKey(part.key);
        return partRole === role && partLanguage === language;
      }) ??
      parts.find((part) => {
        const [partLanguage, partRole] = splitPartKey(part.key);
        return partRole === role && partLanguage === null;
      });
    // A PLAIN parse, not `parseScreeningDocument`: one of the two siblings is
    // `screening_schema`, a role the service publishes NO schema for, so there
    // is no typed reading to ask for. Both are read defensively — a shape this
    // cannot understand contributes no names rather than raising on an
    // unrelated write.
    if (match === undefined) {
      return null;
    }
    try {
      return JSON.parse(match.text) as unknown;
    } catch {
      return null;
    }
  };

  const steps = sibling("screening_steps");
  if (isRecord(steps) && Array.isArray(steps["steps"])) {
    known = true;
    for (const step of steps["steps"]) {
      // `Number.isInteger` excludes the booleans `int` would otherwise admit,
      // matching the service's `not isinstance(step["id"], bool)`.
      if (isRecord(step) && Number.isInteger(step["id"])) {
        roles.add(`step${String(step["id"])}`);
      }
    }
  }

  const schema = sibling("screening_schema");
  if (isRecord(schema) && isRecord(schema["steps"])) {
    known = true;
    for (const name of Object.keys(schema["steps"])) {
      // FILTERED, exactly as the service filters it. `screening_schema` is a
      // PROTECTED role: the service publishes no schema for it, so its `steps`
      // keys are arbitrary clinician-authored JSON that nothing has validated.
      // The service's own union drops anything outside `stepN`
      // (`screening_documents._declared_step_roles`), and a mirror that keeps
      // them would (a) render that unvalidated text as a row heading and two
      // `aria-label`s, and (b) hand `stepRoleCapturedBy` a name like `step`,
      // which prefix-captures EVERY declared step and paints a refusal warning
      // on every row that the service would never raise.
      if (STEP_ROLE.test(name)) {
        roles.add(name);
      }
    }
  }

  return known ? roles : null;
}

/**
 * The mapping fields whose collision test runs in BOTH directions.
 *
 * The service's own `_BIDIRECTIONAL_MAPPINGS`, mirrored rather than inferred.
 */
const BIDIRECTIONAL_MAPPINGS = new Set(["include_mandatory_yeses"]);

/** The two mappings a diagnosis document addresses steps through. */
export type DiagnosisMappingField =
  | "mandatory_roles"
  | "include_mandatory_yeses";

/**
 * The OTHER declared step a mapping key would also be applied to, or `null`.
 *
 * DELIBERATELY THE SERVICE'S OWN RULE (`_step_this_key_would_capture`), mirrored
 * so the collision is visible before Save rather than only after it — INCLUDING
 * the part that surprises: the direction depends on WHICH MAPPING the key is in,
 * so this cannot be answered field-agnostically.
 *
 * FORWARD, for both mappings. `diagnose_logic_process` walks the classification
 * result's steps and, for each, returns the first MAPPING key matching
 * `^<step>`. A key is dangerous when it BEGINS WITH another declared step's
 * name: `{step10: false}` beside a declared `step1` is matched by `^step1`, and
 * step1 silently takes step10's value.
 *
 * REVERSE AS WELL, for `include_mandatory_yeses` only. Its final loop walks the
 * MAPPING's own keys and finds a classification step matching `^<key>`, so
 * `{step1: true}` beside declared `step1` and `step10` resolves to whichever the
 * classification result lists first. That order is the schema's, which this
 * service does not own, so the pair is ambiguous rather than merely unusual.
 *
 * The consequence is asymmetric and is the whole reason for the `field`
 * argument: `step1` beside a declared `step10` is SAFE in `mandatory_roles` and
 * REFUSED in `include_mandatory_yeses`.
 *
 * Compared against the whole declared vocabulary rather than the mapping's own
 * keys: a single-key mapping has nothing to collide with internally and is still
 * ambiguous against the steps around it.
 */
export function stepRoleCapturedBy(
  role: string,
  declared: ReadonlySet<string> | null,
  field: DiagnosisMappingField,
): string | null {
  if (declared === null) {
    return null;
  }
  const bothWays = BIDIRECTIONAL_MAPPINGS.has(field);
  return (
    [...declared]
      .sort()
      .find(
        (other) =>
          other !== role &&
          (role.startsWith(other) || (bothWays && other.startsWith(role))),
      ) ?? null
  );
}

/**
 * Every `stepN` a diagnosis document in this draft refers to.
 *
 * What makes a step id UNSAFE TO REUSE right now. A rule is addressed to a step
 * by id, so handing a newly added step an id some mapping already names would
 * silently apply that rule to a different clinical instruction — see
 * `nextStepId`, which also takes the published history into account.
 */
export function referencedStepIds(parts: readonly DraftPart[]): Set<number> {
  const ids = new Set<number>();
  for (const part of parts) {
    const [, role] = splitPartKey(part.key);
    if (role !== "screening_diagnosis") {
      continue;
    }
    try {
      collectReferencedIds(JSON.parse(part.text), ids);
    } catch {
      continue;
    }
  }
  return ids;
}

/** Every step identity held by the open or loaded draft documents. */
export function screeningStepIds(parts: readonly DraftPart[]): Set<number> {
  const ids = new Set<number>();
  for (const part of parts) {
    const [, role] = splitPartKey(part.key);
    if (role !== "screening_steps") {
      continue;
    }
    try {
      collectStepIds(JSON.parse(part.text), ids);
    } catch {
      continue;
    }
  }
  return ids;
}

function collectStepIds(document: unknown, into: Set<number>): void {
  if (!isRecord(document) || !Array.isArray(document["steps"])) {
    return;
  }
  for (const step of document["steps"]) {
    if (isRecord(step) && Number.isInteger(step["id"])) {
      into.add(step["id"] as number);
    }
  }
}

function collectReferencedIds(document: unknown, into: Set<number>): void {
  if (!isRecord(document)) {
    return;
  }
  for (const field of ["mandatory_roles", "include_mandatory_yeses"]) {
    const mapping = document[field];
    if (!isRecord(mapping)) {
      continue;
    }
    for (const key of Object.keys(mapping)) {
      const match = /^step(\d+)$/.exec(key);
      if (match) {
        into.add(Number(match[1]));
      }
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
